<template>
  <canvas ref="canvas" class="particle-canvas" aria-hidden="true" />
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue'

const canvas = ref(null)
let ctx = null
let rafId = null
let particles = []
let width = 0
let height = 0
let dpr = 1
let running = false
let lastTs = 0
let mouse = { x: 0, y: 0, active: false }
let time = 0

const BASE_PARTICLES = 55
const MAX_SPEED = 0.08
const MAX_SIZE = 3.5
const CONNECT_DIST = 140
const FPS_CAP = 60
const MOUSE_RADIUS = 150
const PULSE_SPEED = 0.001

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

function rand(min, max) { return Math.random() * (max - min) + min }

function resize() {
  if (!canvas.value) return
  const rect = canvas.value.getBoundingClientRect()
  width = Math.max(1, Math.floor(rect.width))
  height = Math.max(1, Math.floor(rect.height))
  dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2))
  canvas.value.width = Math.floor(width * dpr)
  canvas.value.height = Math.floor(height * dpr)
  ctx = canvas.value.getContext('2d', { alpha: true })
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
}

function initParticles() {
  particles = []
  const area = width * height
  const targetCount = Math.min(100, Math.max(30, Math.round(BASE_PARTICLES * (area / (1200 * 800)) ** 0.4)))
  const count = Math.round(targetCount / (dpr ** 0.5))
  
  for (let i = 0; i < count; i++) {
    const depth = rand(0.3, 1) // Add depth for 3D effect
    particles.push({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: rand(-MAX_SPEED, MAX_SPEED) * depth,
      vy: rand(-MAX_SPEED, MAX_SPEED) * depth,
      r: rand(0.8, MAX_SIZE) * depth,
      baseR: rand(0.8, MAX_SIZE) * depth,
      a: rand(0.3, 0.9),
      depth: depth,
      tw: rand(-0.001, 0.001),
      phase: rand(0, Math.PI * 2),
      pulsePhase: rand(0, Math.PI * 2),
      hue: rand(-15, 15), // Slight color variation
      glow: 0,
      connections: new Set()
    })
  }
  // Sort by depth for proper rendering
  particles.sort((a, b) => a.depth - b.depth)
}

function handleMouse(e) {
  const rect = canvas.value.getBoundingClientRect()
  mouse.x = e.clientX - rect.left
  mouse.y = e.clientY - rect.top
  mouse.active = true
}

function handleMouseLeave() {
  mouse.active = false
}

function step(ts) {
  if (!running) return
  const minInterval = 1000 / FPS_CAP
  if (ts && lastTs && ts - lastTs < minInterval) {
    rafId = requestAnimationFrame(step)
    return
  }
  const dt = lastTs ? Math.min(30, ts - lastTs) : 16
  lastTs = ts
  time += dt
  draw(dt)
  rafId = requestAnimationFrame(step)
}

function draw(dt) {
  if (!ctx) return
  
  // Clear with subtle gradient overlay
  ctx.clearRect(0, 0, width, height)
  
  // Create very subtle gradient background
  const gradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, Math.max(width, height))
  gradient.addColorStop(0, 'rgba(62, 190, 169, 0.02)')
  gradient.addColorStop(1, 'rgba(62, 190, 169, 0)')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, width, height)
  
  // Update and draw particles
  for (const p of particles) {
    // Physics update
    p.x += p.vx * dt
    p.y += p.vy * dt
    
    // Smooth alpha oscillation
    p.a += p.tw * dt
    if (p.a < 0.3 || p.a > 0.9) p.tw *= -1
    
    // Pulse effect
    p.pulsePhase += PULSE_SPEED * dt
    const pulseFactor = 1 + Math.sin(p.pulsePhase) * 0.2
    
    // Mouse interaction
    if (mouse.active) {
      const dx = mouse.x - p.x
      const dy = mouse.y - p.y
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist < MOUSE_RADIUS) {
        const force = (1 - dist / MOUSE_RADIUS) * 0.03
        p.vx -= (dx / dist) * force * p.depth
        p.vy -= (dy / dist) * force * p.depth
        p.glow = Math.min(1, p.glow + 0.05)
      } else {
        p.glow = Math.max(0, p.glow - 0.02)
      }
    } else {
      p.glow = Math.max(0, p.glow - 0.02)
    }
    
    // Speed dampening for smoother movement
    p.vx *= 0.999
    p.vy *= 0.999
    
    // Keep particles in bounds with smooth wrapping
    const margin = 20
    if (p.x < -margin) p.x = width + margin
    if (p.x > width + margin) p.x = -margin
    if (p.y < -margin) p.y = height + margin
    if (p.y > height + margin) p.y = -margin
    
    // Draw particle with gradient and glow
    const currentR = p.baseR * pulseFactor * (1 + p.glow * 0.5)
    const alpha = p.a * p.depth
    
    // Outer glow
    if (p.glow > 0 || p.depth > 0.7) {
      const glowGradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, currentR * 4)
      glowGradient.addColorStop(0, `hsla(${170 + p.hue}, 65%, 60%, ${alpha * 0.3 * (1 + p.glow)})`)
      glowGradient.addColorStop(1, 'transparent')
      ctx.fillStyle = glowGradient
      ctx.fillRect(p.x - currentR * 4, p.y - currentR * 4, currentR * 8, currentR * 8)
    }
    
    // Main particle with gradient
    const particleGradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, currentR)
    particleGradient.addColorStop(0, `hsla(${170 + p.hue}, 70%, 65%, ${alpha})`)
    particleGradient.addColorStop(0.5, `hsla(${170 + p.hue}, 65%, 55%, ${alpha * 0.8})`)
    particleGradient.addColorStop(1, `hsla(${170 + p.hue}, 60%, 45%, ${alpha * 0.4})`)
    
    ctx.fillStyle = particleGradient
    ctx.beginPath()
    ctx.arc(p.x, p.y, currentR, 0, Math.PI * 2)
    ctx.fill()
    
    // Inner bright spot for depth
    if (p.depth > 0.6) {
      ctx.fillStyle = `hsla(${170 + p.hue}, 75%, 75%, ${alpha * 0.6})`
      ctx.beginPath()
      ctx.arc(p.x - currentR * 0.2, p.y - currentR * 0.2, currentR * 0.3, 0, Math.PI * 2)
      ctx.fill()
    }
  }
  
  // Draw connections with gradient lines
  const md2 = CONNECT_DIST * CONNECT_DIST
  for (let i = 0; i < particles.length; i++) {
    const connections = 0
    for (let j = i + 1; j < particles.length; j++) {
      if (connections > 3) break // Limit connections per particle
      
      const a = particles[i], b = particles[j]
      const dx = a.x - b.x, dy = a.y - b.y
      const dist2 = dx * dx + dy * dy
      
      if (dist2 < md2 && Math.abs(a.depth - b.depth) < 0.3) {
        const dist = Math.sqrt(dist2)
        const t = 1 - dist / CONNECT_DIST
        
        // Gradient line
        const lineGradient = ctx.createLinearGradient(a.x, a.y, b.x, b.y)
        const baseAlpha = 0.15 * t * Math.min(a.depth, b.depth)
        
        // Pulse the connection
        const pulseFactor = 1 + Math.sin(time * 0.001 + a.phase + b.phase) * 0.3
        const alpha = baseAlpha * pulseFactor
        
        lineGradient.addColorStop(0, `hsla(${170 + a.hue}, 65%, 60%, ${alpha * a.a})`)
        lineGradient.addColorStop(0.5, `hsla(${170 + (a.hue + b.hue) / 2}, 70%, 65%, ${alpha * 1.2})`)
        lineGradient.addColorStop(1, `hsla(${170 + b.hue}, 65%, 60%, ${alpha * b.a})`)
        
        ctx.strokeStyle = lineGradient
        ctx.lineWidth = Math.max(0.5, 1.5 * t * Math.min(a.depth, b.depth))
        ctx.lineCap = 'round'
        ctx.beginPath()
        ctx.moveTo(a.x, a.y)
        
        // Add slight curve to connections for organic feel
        const midX = (a.x + b.x) / 2 + Math.sin(time * 0.0005) * 10
        const midY = (a.y + b.y) / 2 + Math.cos(time * 0.0005) * 10
        ctx.quadraticCurveTo(midX, midY, b.x, b.y)
        ctx.stroke()
      }
    }
  }
  
  ctx.globalAlpha = 1
}

function start() {
  if (running) return
  running = true
  lastTs = 0
  rafId = requestAnimationFrame(step)
}

function stop() {
  running = false
  if (rafId) cancelAnimationFrame(rafId)
  rafId = null
}

function handleVisibility() {
  if (document.hidden) stop()
  else start()
}

onMounted(() => {
  resize()
  initParticles()
  
  if (prefersReducedMotion.matches) {
    particles = particles.slice(0, Math.max(15, Math.floor(particles.length * 0.4)))
    draw(0)
    return
  }
  
  // Event listeners
  canvas.value.addEventListener('mousemove', handleMouse)
  canvas.value.addEventListener('mouseleave', handleMouseLeave)
  
  window.addEventListener('resize', () => {
    const prevCount = particles.length
    resize()
    initParticles()
    particles = particles.slice(0, Math.min(prevCount, particles.length))
  })
  
  document.addEventListener('visibilitychange', handleVisibility)
  
  start()
})

onBeforeUnmount(() => {
  stop()
  canvas.value?.removeEventListener('mousemove', handleMouse)
  canvas.value?.removeEventListener('mouseleave', handleMouseLeave)
  document.removeEventListener('visibilitychange', handleVisibility)
})
</script>

<style scoped>
.particle-canvas {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: auto; /* Enable mouse interaction */
  width: 100vw;
  height: 100vh;
  display: block;
  opacity: 1;
  background: transparent;
  /* Add subtle animation for the canvas itself */
  animation: canvasGlow 8s ease-in-out infinite;
}

@keyframes canvasGlow {
  0%, 100% { filter: brightness(1) contrast(1); }
  50% { filter: brightness(1.05) contrast(1.02); }
}

/* Performance optimization for smooth rendering */
.particle-canvas {
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}
</style>